import { ChangeEvent, useRef, useState } from "react";
import { toast } from "sonner";
import { GripVertical, ImagePlus, Link2, Star, Trash2 } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useAddRoomPhotoFromUrl,
  useDeleteRoomPhoto,
  useReorderRoomPhotos,
  useSetCoverRoomPhoto,
  useUploadRoomPhotos,
  type RoomPhoto,
} from "@/hooks/useRooms";

interface BusinessPhoto {
  id: string;
  url: string;
  isCover: boolean;
}

interface SortableTileProps {
  photo: RoomPhoto;
  onDelete: () => void;
  onSetCover: () => void;
}

const SortableTile = ({ photo, onDelete, onSetCover }: SortableTileProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      className={`relative rounded-md overflow-hidden border w-24 h-24 shrink-0 ${photo.isCover ? "border-primary ring-2 ring-primary/40" : "border-border"}`}
    >
      <img src={photo.url} alt="" className="w-full h-full object-cover" />
      <button
        type="button"
        className="absolute top-0.5 left-0.5 p-0.5 bg-black/50 rounded text-white/70 hover:text-white cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-3 h-3" />
      </button>

      <div className="absolute top-0.5 right-0.5 flex gap-1">
        <button
          type="button"
          onClick={onSetCover}
          className={`p-0.5 rounded ${photo.isCover ? "bg-amber-500 text-white" : "bg-black/50 text-white/70 hover:text-white"}`}
          title={photo.isCover ? "Cover image" : "Set as cover image"}
        >
          <Star className="w-3 h-3 fill-current" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="p-0.5 bg-black/50 rounded text-white/70 hover:text-white"
          title="Remove"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {photo.isCover && (
        <span className="absolute bottom-0.5 left-0.5 text-[8px] font-bold bg-primary text-primary-foreground px-1 rounded">
          Cover
        </span>
      )}
    </div>
  );
};

interface Props {
  roomId: string;
  businessId: string;
  photos: RoomPhoto[];
  businessPhotos: BusinessPhoto[];
}

export const RoomPhotosPicker = ({
  roomId,
  businessId,
  photos,
  businessPhotos,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPicker, setShowPicker] = useState(false);
  const uploadMut = useUploadRoomPhotos(businessId);
  const deleteMut = useDeleteRoomPhoto(businessId);
  const reorderMut = useReorderRoomPhotos(businessId);
  const setCoverMut = useSetCoverRoomPhoto(businessId);
  const fromUrlMut = useAddRoomPhotoFromUrl(businessId);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const canAdd = photos.length < 10;

  const onUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    try {
      await uploadMut.mutateAsync({ roomId, files });
      toast.success(`Uploaded ${files.length} photo(s)`);
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed");
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDelete = async (photoId: string) => {
    try {
      await deleteMut.mutateAsync({ roomId, photoId });
    } catch (err: any) {
      toast.error(err?.message ?? "Delete failed");
    }
  };

  const onSetCover = async (photoId: string) => {
    try {
      await setCoverMut.mutateAsync({ roomId, photoId });
      toast.success("Cover image set");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to set cover image");
    }
  };

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const sorted = [...photos].sort((a, b) => a.sortOrder - b.sortOrder);
    const oldIdx = sorted.findIndex((p) => p.id === active.id);
    const newIdx = sorted.findIndex((p) => p.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = [...sorted];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    try {
      await reorderMut.mutateAsync({
        roomId,
        order: reordered.map((p) => p.id),
      });
    } catch (err: any) {
      toast.error(err?.message ?? "Reorder failed");
    }
  };

  const onPickBusinessPhoto = async (url: string) => {
    try {
      await fromUrlMut.mutateAsync({ roomId, url });
      toast.success("Photo added from library");
      setShowPicker(false);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to add photo");
    }
  };

  const sorted = [...photos].sort(
    (a, b) =>
      (b.isCover ? 1 : 0) - (a.isCover ? 1 : 0) || a.sortOrder - b.sortOrder,
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground font-medium">
          Room Gallery & Photos
        </p>
        <span className="text-[11px] text-muted-foreground">
          {photos.length} photo(s)
        </span>
      </div>

      <div className="flex flex-wrap gap-2 items-start">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={sorted.map((p) => p.id)}
            strategy={rectSortingStrategy}
          >
            {sorted.map((p) => (
              <SortableTile
                key={p.id}
                photo={p}
                onDelete={() => onDelete(p.id)}
                onSetCover={() => onSetCover(p.id)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {canAdd && (
          <div className="flex gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploadMut.isPending}
              className="h-24 w-14 flex-col gap-1 px-1 text-[10px]"
            >
              <ImagePlus className="w-4 h-4" />
              Upload
            </Button>
            {businessPhotos.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPicker(true)}
                disabled={fromUrlMut.isPending}
                className="h-24 w-14 flex-col gap-1 px-1 text-[10px]"
              >
                <Link2 className="w-4 h-4" />
                Library
              </Button>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={onUpload}
        />
      </div>

      <Dialog open={showPicker} onOpenChange={setShowPicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Media Library (Reuse Image)</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-3 max-h-80 overflow-y-auto p-1">
            {businessPhotos.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onPickBusinessPhoto(p.url)}
                className="relative rounded overflow-hidden border border-border hover:ring-2 hover:ring-primary/50 transition-all text-left group"
              >
                <img src={p.url} alt="" className="w-full h-24 object-cover" />
                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded font-medium">
                    Add
                  </span>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
